import React, { useState, useEffect, useRef } from "react";

const TeamMemberCard = ({ name, title, description, imageUrl }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isCentered, setIsCentered] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const cardRef = useRef(null);

    // Detect if device is mobile
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 768);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Intersection Observer for mobile center detection
    useEffect(() => {
        if (!isMobile || !cardRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const rect = entry.boundingClientRect;
                        const viewportHeight = window.innerHeight;
                        const viewportCenter = viewportHeight / 2;
                        
                        // Calculate card center position
                        const cardTop = rect.top;
                        const cardBottom = rect.bottom;
                        const cardCenter = cardTop + (cardBottom - cardTop) / 2;
                        
                        // Calculate distance from viewport center
                        const distanceFromCenter = Math.abs(cardCenter - viewportCenter);
                        
                        // More lenient threshold - trigger when card center is within 35% of viewport center
                        const centerThreshold = viewportHeight * 0.35;
                        
                        // Additional check: make sure a good portion of the card is visible
                        const cardVisibleHeight = Math.min(cardBottom, viewportHeight) - Math.max(cardTop, 0);
                        const visibilityRatio = cardVisibleHeight / rect.height;
                        
                        setIsCentered(
                            distanceFromCenter < centerThreshold && 
                            visibilityRatio > 0.6 // At least 60% of card is visible
                        );
                    } else {
                        setIsCentered(false);
                    }
                });
            },
            {
                threshold: [0, 0.1, 0.3, 0.5, 0.7, 0.9, 1],
                rootMargin: '0px'
            }
        );

        observer.observe(cardRef.current);

        return () => observer.disconnect();
    }, [isMobile]);

    // Determine if animation should be active
    const isAnimationActive = isMobile ? isCentered : isHovered;

  return (
    <div 
      ref={cardRef}
      className={`relative h-[500px] cursor-pointer group overflow-hidden rounded-t-lg`} 
      onMouseEnter={() => !isMobile && setIsHovered(true)} 
      onMouseLeave={() => !isMobile && setIsHovered(false)}
    >
      {/* Image Section - Full Height */}
      <div className="absolute inset-0 overflow-hidden bg-[#65806E]">
        <img 
          src={imageUrl} 
          alt={name} 
          className={`w-full h-full object-cover transition-all duration-[1000ms] ${
            isAnimationActive ? 'scale-110' : 'scale-100'
          }`}
        />
      </div>

      {/* Gradient Overlay */}
      <div className={`absolute inset-0 bg-gradient-to-t from-black via-black/50 via-[40%] to-transparent`} />

      {/* Content Section - Always Overlayed */}
      <div className={`absolute inset-0 flex flex-col justify-end text-start p-6 transition-all duration-[1200ms]${
        isAnimationActive ? 'pb-8' : 'pb-6'
      }`}>
        
        {/* Name - Always visible */}
        <h3 className={`text-2xl font-bold text-white panoragraf-light mb-2 transition-all  duration-[1200ms] ${
          isAnimationActive 
            ? 'opacity-100 transform translate-y-0' 
            : 'opacity-95 transform translate-y-1'
        }`}>
          {name}
        </h3>
        
        {/* Title - Partially hidden initially */}
        <h4 className={`text-neutral-400 text-lg font-medium mb-3 transition-all line-clamp-1 group-hover:line-clamp-none duration-[1200ms] ${
          isAnimationActive 
            ? 'opacity-100 transform translate-y-0' 
            : 'opacity-70 transform translate-y-2'
        }`}>
          {title}
        </h4>
        
        {/* Description - Hidden initially, revealed on animation */}
        <div className={`transition-all duration-[1200ms] ${
          isAnimationActive 
            ? 'max-h-[200px] opacity-100 transform translate-y-0' 
            : 'max-h-0 opacity-0 transform translate-y-4 overflow-hidden'
        }`}>
          <p className={`text-neutral-100 text-sm leading-relaxed transition-all duration-[1200ms] ${
            description === '' ? 'min-h-[0rem]' : ''
          }`}>
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberCard;